import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  positionUnwinderAxiosApi,
  predictionManagerAxiosApi
} from 'src/Config/axiosApi';
import {
  UnwindingRule,
  UnwindingRuleType,
  UnwindingExecution,
  CreateUnwindingRuleDto,
  UpdateUnwindingRuleDto,
  OrdersResponse
} from '../types/unwinding.types';
import { normalizeOrdersPayload } from '../utils/normalizeOrders';
import { recoverFromDuplicateCreate, seedRuleIntoLists } from './ruleCreation';
import {
  getUnwinderState,
  stopUnwinder,
  startUnwinder,
  clearUnwinderPanicLockout,
  UnwinderState
} from 'src/Api/exchanges';

export function useUnwindingRules(opts?: { ruleType?: UnwindingRuleType }) {
  const ruleType = opts?.ruleType;
  return useQuery<UnwindingRule[]>(
    ['unwinding-rules', ruleType ?? 'all'],
    async () => {
      const { data } = await positionUnwinderAxiosApi.get<UnwindingRule[]>(
        '/rules',
        ruleType ? { params: { ruleType } } : undefined
      );
      return data;
    },
    { refetchInterval: 5000, keepPreviousData: true }
  );
}

export function useCreateRule() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: CreateUnwindingRuleDto) => {
      try {
        const { data } = await positionUnwinderAxiosApi.post<UnwindingRule>(
          '/rules',
          dto
        );
        return data;
      } catch (err) {
        // Duplicate-create conflict (409 + existingRuleId) means the pair
        // already has a rule of this type — resolve to it so the caller
        // proceeds with its toggle-platform PATCH instead of surfacing a
        // failure toast. Anything else rethrows untouched. Mirrors
        // `useCreateEntryRule` on the opener.
        return recoverFromDuplicateCreate(err, async id => {
          const { data } = await positionUnwinderAxiosApi.get<UnwindingRule>(
            `/rules/${id}`
          );
          return data;
        });
      }
    },
    {
      mutationKey: 'unwinding:create-rule',
      onSuccess: created => {
        // Seed the rule into every cached `unwinding-rules` list so rows
        // bind to it the moment the create resolves. Invalidation alone
        // left a ~1-5s window where the row still saw `rule === undefined`
        // and a second toggle fired a duplicate create for the same pair.
        // Legacy docs without ruleType default to 'tna' — same slicing the
        // page applies.
        seedRuleIntoLists(
          qc.getQueriesData<UnwindingRule[]>('unwinding-rules'),
          created,
          { defaultRuleType: 'tna' }
        ).forEach(([key, data]) => qc.setQueryData(key, data));
        qc.invalidateQueries('unwinding-rules');
      }
    }
  );
}

export function useUpdateRule() {
  const qc = useQueryClient();
  return useMutation(
    async ({ id, dto }: { id: string; dto: UpdateUnwindingRuleDto }) => {
      const { data } = await positionUnwinderAxiosApi.patch<UnwindingRule>(
        `/rules/${id}`,
        dto
      );
      return data;
    },
    {
      mutationKey: 'unwinding:update-rule-params',
      onSuccess: () => {
        qc.invalidateQueries('unwinding-rules');
      }
    }
  );
}

/**
 * Bulk parameter update across EXISTING sum_of_sides unwinding rules in one
 * request. `scope: 'all'` updates every sum_of_sides rule; `scope: 'ids'`
 * updates just the supplied `ruleIds`. Only the filled params (`thresholdSum`
 * / `size` / `maxVolumeContracts` / `makerSizeMode` /
 * `counterStuckMarketHedgeSeconds`) are changed — an absent
 * field is left untouched server-side. Mirrors `useUpdateRule` (same `positionUnwinderAxiosApi`
 * + `unwinding-rules` invalidation); rule-less rows are staged client-side by
 * the caller, not created here.
 */
export function useBulkUpdateUnwindingRuleParams() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: {
      scope?: 'all' | 'ids';
      ruleIds?: string[];
      thresholdSum?: number;
      size?: number;
      maxVolumeContracts?: number;
      makerSizeMode?: 'dynamic' | 'fixed';
      counterStuckMarketHedgeSeconds?: number;
      counterMakerWalkStepSeconds?: number | null;
      counterMakerWalkStepCents?: number | null;
      lockstepMaxAheadBatches?: number | null;
    }) => {
      const { data } = await positionUnwinderAxiosApi.patch<{
        scope: string;
        matched: number;
        modified: number;
      }>('/rules/bulk-params', dto);
      return data;
    },
    {
      mutationKey: 'unwinding:bulk-params',
      onSuccess: () => {
        qc.invalidateQueries('unwinding-rules');
      }
    }
  );
}

export function useDeleteRule() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      await positionUnwinderAxiosApi.delete(`/rules/${id}`);
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('unwinding-rules');
      }
    }
  );
}

/**
 * Optimistic patcher shared by activate / pause / toggle-platform.
 * Walks every cached `unwinding-rules` query and patches the matching
 * rule by id. Returns a rollback fn the caller stashes for `onError`.
 *
 * Why: previously these mutations called `qc.invalidateQueries(...)`
 * on success, which kicked a fresh GET /rules round-trip that the
 * operator had to wait through (visible as a 1-3s lag on toggle).
 * The WS broadcast already pushes the authoritative state on the
 * next tick, so the cache patch is enough — we drop the invalidation
 * entirely. Rollback on error keeps the cache honest if the server
 * rejects.
 */
function patchUnwindingRulesCache(
  qc: ReturnType<typeof useQueryClient>,
  ruleId: string,
  patch: Partial<UnwindingRule>,
): () => void {
  const snapshots: Array<{ key: unknown; data: UnwindingRule[] | undefined }> =
    [];
  qc.getQueriesData<UnwindingRule[]>('unwinding-rules').forEach(
    ([key, data]) => {
      if (!Array.isArray(data)) return;
      snapshots.push({ key, data });
      qc.setQueryData<UnwindingRule[]>(
        key as any,
        data.map((r) => (r._id === ruleId ? { ...r, ...patch } : r)),
      );
    },
  );
  return () => {
    snapshots.forEach(({ key, data }) =>
      qc.setQueryData(key as any, data),
    );
  };
}

export function useActivateRule() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await positionUnwinderAxiosApi.post<UnwindingRule>(
        `/rules/${id}/activate`
      );
      return data;
    },
    {
      onMutate: async (id) => {
        await qc.cancelQueries('unwinding-rules');
        return { rollback: patchUnwindingRulesCache(qc, id, { status: 'active' as any }) };
      },
      onError: (_e, _v, ctx: any) => ctx?.rollback?.(),
    }
  );
}

export function usePauseRule() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await positionUnwinderAxiosApi.post<UnwindingRule>(
        `/rules/${id}/pause`
      );
      return data;
    },
    {
      mutationKey: 'unwinding:pause',
      onMutate: async (id) => {
        await qc.cancelQueries('unwinding-rules');
        return { rollback: patchUnwindingRulesCache(qc, id, { status: 'paused' as any }) };
      },
      onError: (_e, _v, ctx: any) => ctx?.rollback?.(),
    }
  );
}

export function useRuleExecutions(ruleId: string | null) {
  return useQuery<UnwindingExecution[]>(
    ['unwinding-executions', ruleId],
    async () => {
      if (!ruleId) return [];
      const { data } = await positionUnwinderAxiosApi.get<
        UnwindingExecution[]
      >(`/rules/${ruleId}/executions`);
      return data;
    },
    {
      enabled: !!ruleId,
      refetchInterval: 10000,
      keepPreviousData: true
    }
  );
}

/**
 * Per-pair unwinder kill switch — flips `prediction_pairs.unwinderEnabled`.
 * Default is true on every pair; flipping to false auto-pauses the rule and
 * lets the pruner drop the WS books within ~5 min. The UI must show a
 * confirmation dialog when an open position exists on either leg — toggling
 * off doesn't close the position, it just removes the pair from the unwinder's
 * managed set (operator handles it manually thereafter). State comes back via
 * the `unwinding_state_update` envelope so no optimistic cache patch.
 */
export function useSetPairUnwinderEnabled() {
  return useMutation(
    async ({ pairId, enabled }: { pairId: string; enabled: boolean }) => {
      const { data } = await predictionManagerAxiosApi.patch(
        `/pairs/${pairId}/unwinder-enabled`,
        { enabled }
      );
      return data;
    },
    { mutationKey: 'unwinding:set-pair-unwinder-enabled' }
  );
}

/**
 * Explicit arm/disarm intent for an unwinding rule — the mirror of the opener's
 * `useSetRuleArmed`. The operator's on/off gesture sends a single explicit
 * boolean — `PATCH /rules/{id}` with `{ desiredArmed }` — and that boolean is
 * the ONLY thing that arms or pauses the rule durably. The backend no longer
 * INFERS arming from a platform-flag flip, so a stale/other-writer update can
 * never silently re-arm a rule the operator turned off. The per-leg platform
 * toggles stay pure per-leg config. Routed to the position-unwinder service
 * (same base as every other per-rule mutation). State comes back via the
 * `unwinding_state_update` envelope's `ruleArmed`, so no optimistic cache patch.
 */
export function useSetUnwinderRuleArmed() {
  const qc = useQueryClient();
  return useMutation(
    async ({ id, desiredArmed }: { id: string; desiredArmed: boolean }) => {
      const { data } = await positionUnwinderAxiosApi.patch<UnwindingRule>(
        `/rules/${id}`,
        { desiredArmed }
      );
      return data;
    },
    {
      mutationKey: 'unwinding:set-armed',
      onSuccess: () => {
        qc.invalidateQueries('unwinding-rules');
      }
    }
  );
}

/**
 * Bulk per-pair unwinder kill switch — flips `prediction_pairs.unwinderEnabled`
 * on many pairs in one round trip. `scope: 'ids'` flips the given pairIds;
 * `scope: 'all'` flips every active non-archived pair server-side. Same
 * cascade as the single-pair `useSetPairUnwinderEnabled` (auto-pause rules +
 * WS prune within ~5 min); live state rides the `unwinding_state_update`
 * envelope so no optimistic cache patch is needed.
 */
export function useBulkSetPairUnwinderEnabled() {
  return useMutation(
    async (vars: {
      pairIds?: string[];
      scope?: 'ids' | 'all';
      enabled: boolean;
    }) => {
      const { data } = await predictionManagerAxiosApi.patch(
        '/pairs/bulk-unwinder-enabled',
        vars
      );
      return data as {
        scope: 'ids' | 'all';
        matched: number;
        modified: number;
      };
    },
    { mutationKey: 'unwinding:bulk-unwinder-enabled' }
  );
}

/**
 * Resilient panic: fans out to BOTH the position-unwinder /rules/panic AND
 * the prediction-manager /api/system/panic (which pauses every active
 * unwinding rule directly in Mongo + best-effort kicks the service again).
 * `Promise.allSettled` so one unreachable pod cannot leave the system armed
 * — at least one of the two paths is enough to guarantee no new orders.
 */
export function usePanic() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const [serviceRes, managerRes] = await Promise.allSettled([
        positionUnwinderAxiosApi.post('/rules/panic'),
        predictionManagerAxiosApi.post('/api/system/panic', { target: 'unwinder' })
      ]);
      if (serviceRes.status === 'rejected' && managerRes.status === 'rejected') {
        throw new Error(
          `Both panic paths failed — service: ${serviceRes.reason}; manager: ${managerRes.reason}`
        );
      }
      return {
        service:
          serviceRes.status === 'fulfilled' ? serviceRes.value.data : null,
        manager:
          managerRes.status === 'fulfilled' ? managerRes.value.data : null
      };
    },
    { onSuccess: () => { qc.invalidateQueries('unwinding-rules'); } }
  );
}

/**
 * Soft panic (24h): same resilient fan-out shape as `usePanic`, but cancels
 * orders only on markets of unwinding rules active in the last 24h. Orders
 * on unrelated markets are left untouched.
 */
export function usePanicRecent() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const [serviceRes, managerRes] = await Promise.allSettled([
        positionUnwinderAxiosApi.post('/rules/panic-recent'),
        predictionManagerAxiosApi.post('/api/system/panic-recent', { target: 'unwinder' })
      ]);
      if (serviceRes.status === 'rejected' && managerRes.status === 'rejected') {
        throw new Error(
          `Both soft-panic paths failed — service: ${serviceRes.reason}; manager: ${managerRes.reason}`
        );
      }
      return {
        service:
          serviceRes.status === 'fulfilled' ? serviceRes.value.data : null,
        manager:
          managerRes.status === 'fulfilled' ? managerRes.value.data : null
      };
    },
    { onSuccess: () => { qc.invalidateQueries('unwinding-rules'); } }
  );
}

export function useResume() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } = await positionUnwinderAxiosApi.post('/rules/resume');
      return data;
    },
    { onSuccess: () => { qc.invalidateQueries('unwinding-rules'); } }
  );
}

export function useGlobalToggle() {
  const qc = useQueryClient();
  return useMutation(
    async (enabled: boolean) => {
      const { data } = await positionUnwinderAxiosApi.post('/rules/global-toggle', { enabled });
      return data;
    },
    { onSuccess: () => { qc.invalidateQueries('unwinding-rules'); } }
  );
}

export function useTogglePlatform() {
  const qc = useQueryClient();
  return useMutation(
    async ({ id, platform, enabled }: { id: string; platform: 'kalshi' | 'polymarket'; enabled: boolean }) => {
      const { data } = await positionUnwinderAxiosApi.post(`/rules/${id}/toggle-platform`, { platform, enabled });
      return data;
    },
    {
      mutationKey: 'unwinding:toggle-platform',
      onMutate: async ({ id, platform, enabled }) => {
        await qc.cancelQueries('unwinding-rules');
        const patch: Partial<UnwindingRule> =
          platform === 'kalshi'
            ? { kalshiEnabled: enabled }
            : { polymarketEnabled: enabled };
        return { rollback: patchUnwindingRulesCache(qc, id, patch) };
      },
      onError: (_e, _v, ctx: any) => ctx?.rollback?.(),
    }
  );
}

/**
 * Group-scoped bulk platform toggle. Flips `kalshiEnabled`/`polymarketEnabled`
 * across every unwinding rule whose pair is in the given opener group
 * (`scope: 'all'` + `groupId`). enable=true may activate rules, but the server
 * applies a positions-sync gate per rule — rules that fail it are counted in
 * `skipped` (partial success). Mirrors `useTogglePlatform` (same
 * `positionUnwinderAxiosApi` + `unwinding-rules` invalidation); the global
 * MutationCache.onError backstop surfaces failures.
 */
export function useBulkSetPlatform() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: {
      scope?: 'all' | 'ids';
      ruleIds?: string[];
      groupId?: string;
      platform: 'kalshi' | 'polymarket';
      enabled: boolean;
    }) => {
      const { data } = await positionUnwinderAxiosApi.patch<{
        scope: 'all' | 'ids';
        matched: number;
        modified: number;
        skipped: number;
      }>('/rules/bulk-platform', dto);
      return data;
    },
    {
      mutationKey: 'unwinding:bulk-set-platform',
      onSuccess: () => {
        qc.invalidateQueries('unwinding-rules');
      }
    }
  );
}

export function useSetSimParams() {
  return useMutation(
    async ({
      pairId,
      tna,
      size,
      maxVol,
    }: {
      pairId: string;
      tna: number;
      size: number;
      maxVol?: number;
    }) => {
      const { data } = await positionUnwinderAxiosApi.post('/rules/sim-params', {
        pairId,
        tna,
        size,
        maxVol,
      });
      return data;
    }
  );
}

export function useApplySim() {
  return useMutation(async ({ pairId }: { pairId: string }) => {
    const { data } = await positionUnwinderAxiosApi.post(
      '/rules/apply-sim',
      { pairId }
    );
    return data;
  });
}

export function useOrders() {
  return useQuery<OrdersResponse>(
    'unwinding-orders',
    async () => {
      const { data } = await positionUnwinderAxiosApi.get('/orders');
      return normalizeOrdersPayload(data);
    },
    { refetchInterval: 3000, keepPreviousData: true }
  );
}

export function useUnwinderState() {
  return useQuery<UnwinderState>(
    ['unwinder-state'],
    getUnwinderState,
    {
      // Poll every 3s so the Stop/Start button flips promptly after a scale
      // patch lands on the Deployment.
      refetchInterval: 3_000,
      keepPreviousData: true,
    }
  );
}

export function useStopUnwinder() {
  const qc = useQueryClient();
  return useMutation(stopUnwinder, {
    onSuccess: job => {
      // Seed system/job so UpdatesProvider's stepper picks it up immediately.
      // UnwindingPage is wrapped in `<UpdatesProvider scope="predictions">`,
      // and the provider's job-poll query key is `['system/job', scope]` —
      // a string-only key like 'system/job' silently misses, leaving the
      // stepper popover invisible until the next 2s poll tick.
      qc.setQueryData(['system/job', 'predictions'], job);
      qc.invalidateQueries(['system/job', 'predictions']);
      qc.invalidateQueries('unwinding-rules');
      qc.invalidateQueries('unwinding-orders');
      qc.invalidateQueries(['unwinder-state']);
    }
  });
}

export function useStartUnwinder() {
  const qc = useQueryClient();
  return useMutation(startUnwinder, {
    onSuccess: job => {
      qc.setQueryData(['system/job', 'predictions'], job);
      qc.invalidateQueries(['system/job', 'predictions']);
      qc.invalidateQueries(['unwinder-state']);
    }
  });
}

export function useClearUnwinderPanicLockout() {
  const qc = useQueryClient();
  return useMutation(clearUnwinderPanicLockout, {
    onSuccess: () => {
      // Re-fetch state so the lockout banner clears + START re-enables.
      qc.invalidateQueries(['unwinder-state']);
    }
  });
}
